import "../../../../css/tiptap.css";
import articleService from "@/features/dashboard/api/services/article-service";
import {
	IconBrandYoutube,
	IconColumnInsertLeft,
	IconColumnInsertRight,
	IconColumnRemove,
	IconCornerDownLeft,
	IconPhotoPlus,
	IconRowInsertBottom,
	IconRowInsertTop,
	IconRowRemove,
	IconTableMinus,
	IconTablePlus,
} from "@tabler/icons-react";
import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Code from "@tiptap/extension-code";
import Document from "@tiptap/extension-document";
import Gapcursor from "@tiptap/extension-gapcursor";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import History from "@tiptap/extension-history";
import Image from "@tiptap/extension-image";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Youtube from "@tiptap/extension-youtube";
// => Tiptap packages
import { BubbleMenu, type Editor, EditorContent, useEditor } from "@tiptap/react";
import classNames from "classnames";
import type React from "react";
import { useCallback, useState } from "react";
import ImageResize from "tiptap-extension-resize-image";
// import { TextStyle } from '@tiptap/extension-text-style';
import { FontSize } from "./FontSize";
// Custom
// import content from "./content";
import * as Icons from "./Icons";

// import { LinkModal } from "./LinkModal";

interface TiptapProps {
	content: string;
	onUpdate?: (html: string) => void;
	disabled?: boolean;
	className?: string;
}

export function Tiptap({ content, onUpdate, disabled = false, className }: TiptapProps) {
	const [height, setHeight] = useState(480);
	const [width, setWidth] = useState(640);
	const editor = useEditor({
		extensions: [
			Document,
			History,
			Paragraph,
			ImageResize.configure({
				inline: true,
			}),
			// Image.configure({
			// 	inline: true,
			// }),
			Text,
			Link.configure({
				openOnClick: false,
			}),
			Bold,
			Underline,
			BulletList,
			OrderedList,
			Heading.configure({
				levels: [1, 2, 3],
			}),
			Italic,
			ListItem,
			Strike,
			Code,
			FontSize,
			TextAlign.configure({
				types: ["heading", "paragraph"],
			}),
			Gapcursor,
			Table.configure({
				resizable: true,
			}),
			TableRow,
			TableHeader,
			TableCell,
			Blockquote,
			Youtube,
			HardBreak,
		],
		content,
		onUpdate({ editor }) {
			if (onUpdate) {
				onUpdate(editor.getHTML());
			}
		},
	}) as Editor;
	const [fontSize, setFontSize] = useState("12px");
	const [url, setUrl] = useState<string>("");

	const setLink = useCallback(() => {
		const previousUrl = editor.getAttributes("link").href;
		const url = window.prompt("URL", previousUrl);

		// cancelled
		if (url === null) {
			return;
		}

		// empty
		if (url === "") {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();

			return;
		}

		// update link
		try {
			editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
		} catch (e) {
			alert(e.message);
		}
	}, [editor]);

	const removeLink = useCallback(() => {
		editor.chain().focus().extendMarkRange("link").unsetLink().run();
	}, [editor]);

	const toggleBulletList = useCallback(() => {
		editor.chain().focus().toggleBulletList().run();
	}, [editor]);

	const toggleOrderedList = useCallback(() => {
		editor.chain().focus().toggleOrderedList().run();
	}, [editor]);

	const toggleH1 = useCallback(() => {
		editor.chain().focus().toggleHeading({ level: 1 }).run();
	}, [editor]);

	const toggleH2 = useCallback(() => {
		editor.chain().focus().toggleHeading({ level: 2 }).run();
	}, [editor]);

	const toggleLeft = useCallback(() => {
		editor.chain().focus().setTextAlign("left").run();
	}, [editor]);

	const toggleRight = useCallback(() => {
		editor.chain().focus().setTextAlign("right").run();
	}, [editor]);

	const toggleCenter = useCallback(() => {
		editor.chain().focus().setTextAlign("center").run();
	}, [editor]);

	const toggleJustify = useCallback(() => {
		editor.chain().focus().setTextAlign("justify").run();
	}, [editor]);

	const toggleH3 = useCallback(() => {
		editor.chain().focus().toggleHeading({ level: 3 }).run();
	}, [editor]);

	const toggleBold = useCallback(() => {
		editor.chain().focus().toggleBold().run();
	}, [editor]);

	const toggleUnderline = useCallback(() => {
		editor.chain().focus().toggleUnderline().run();
	}, [editor]);

	const toggleItalic = useCallback(() => {
		editor.chain().focus().toggleItalic().run();
	}, [editor]);

	const toggleStrike = useCallback(() => {
		editor.chain().focus().toggleStrike().run();
	}, [editor]);

	const toggleParagraph = useCallback(() => {
		editor.chain().focus().toggleParagraph().run();
	}, [editor]);

	// const addImage = () => {
	//   const url = window.prompt('URL')

	//   if (url) {
	//     editor.chain().focus().setImage({ src: url }).run()
	//   }
	// }

	const handleFontSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setFontSize(event.target.value);
		toggleFont(event.target.value);
	};

	const toggleFont = useCallback(
		(fontSize) => {
			editor.chain().focus().setFontSize(fontSize).run();
		},
		[editor],
	);

	const toggleCode = useCallback(() => {
		editor.chain().focus().toggleCode().run();
	}, [editor]);

	const addYoutubeVideo = () => {
		const url = prompt("Enter YouTube URL");

		if (url) {
			editor.commands.setYoutubeVideo({
				src: url,
				width: Math.max(320, Number.parseInt(width.toString(), 10)) || 640,
				height: Math.max(180, Number.parseInt(height.toString(), 10)) || 480,
			});
		}
	};

	const setHardBreak = useCallback(() => {
		editor.chain().focus().setHardBreak().run();
	}, [editor]);

	if (!editor) {
		return null;
	}

	// Adding image to the editor
	const addImage = (url: string) => {
		if (url) {
			editor.chain().focus().setImage({ src: url }).run();
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e?.target?.files?.[0]) return;
		articleService
			.uploadImage(e.target.files[0])
			.then((res) => addImage(res))
			.catch((err) => console.error(err));
	};

	const insertTable = useCallback(() => {
		editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
	}, [editor]);

	const deleteTable = useCallback(() => {
		editor.chain().focus().deleteTable().run();
	}, [editor]);

	const addColumnBefore = useCallback(() => {
		editor.chain().focus().addColumnBefore().run();
	}, [editor]);

	const addColumnAfter = useCallback(() => {
		editor.chain().focus().addColumnAfter().run();
	}, [editor]);

	const deleteColumn = useCallback(() => {
		editor.chain().focus().deleteColumn().run();
	}, [editor]);

	const addRowBefore = useCallback(() => {
		editor.chain().focus().addRowBefore().run();
	}, [editor]);

	const addRowAfter = useCallback(() => {
		editor.chain().focus().addRowAfter().run();
	}, [editor]);

	const mergeCells = useCallback(() => {
		editor.chain().focus().mergeCells().run();
	}, [editor]);

	const splitCell = useCallback(() => {
		editor.chain().focus().splitCell().run();
	}, [editor]);

	const deleteRow = useCallback(() => {
		editor.chain().focus().deleteRow().run();
	}, [editor]);

	const toggleHeaderColumn = useCallback(() => {
		editor.chain().focus().toggleHeaderColumn().run();
	}, [editor]);

	const toggleHeaderRow = useCallback(() => {
		editor.chain().focus().toggleHeaderRow().run();
	}, [editor]);

	const toggleHeaderCell = useCallback(() => {
		editor.chain().focus().toggleHeaderCell().run();
	}, [editor]);

	const mergeOrSplit = useCallback(() => {
		editor.chain().focus().mergeOrSplit().run();
	}, [editor]);

	const toggleBlockquote = useCallback(() => {
		editor.chain().focus().toggleBlockquote().run();
	}, [editor]);

	const setBlockquote = useCallback(() => {
		editor.chain().focus().setBlockquote().run();
	}, [editor]);

	const unsetBlockquote = useCallback(() => {
		editor.chain().focus().unsetBlockquote().run();
	}, [editor]);

	return (
		<div
			className={classNames(
				"editor bg-background text-foreground rounded-md border-border border focus-within:border-focus-border focus-within:ring-2 ring-offset-0 focus-within:ring-focus-ring/30 outline-0",
				className,
			)}
		>
			{/* <div className="menu"> */}
			<div className="flex p-1 border-b border-border rounded-t-md bg-slate-50">
				<div className="flex overflow-y-scroll flex-row gap-2 items-center px-2 divide-x divide-neutral-900/20">
					<select
						className="rounded-md border-none text-gray-900 text-sm bg-transparent focus:ring-1 focus:ring-primary-900"
						value={fontSize}
						onChange={handleFontSizeChange}
					>
						<option value="14px">Normal Text</option>
						<option value="12px">Subtitle</option>
						<option value="18px">Title</option>
						<option value="20px">Header</option>
						{/* <option value="20px">20px</option> */}
					</select>

					<div className="flex flex-row gap-3 px-2">
						<label className="inline-block px-2 pt-1 cursor-pointer" htmlFor="upload">
							<IconPhotoPlus className="h-4 w-4" />
							<input className="hidden" id="upload" type="file" onChange={handleChange} />
						</label>
						<button
							onClick={toggleH1}
							className={classNames("font-light", {
								"is-active": editor.isActive("heading", { level: 1 }),
							})}
							type="button"
						>
							H1
						</button>
						<button
							onClick={toggleH2}
							className={classNames("font-light", {
								"is-active": editor.isActive("heading", { level: 2 }),
							})}
							type="button"
						>
							H2
						</button>
						<button
							onClick={toggleH3}
							className={classNames("font-light", {
								"is-active": editor.isActive("heading", { level: 3 }),
							})}
							type="button"
						>
							H3
						</button>
					</div>
					<div className="flex flex-row gap-3 px-2">
						<button
							className={classNames("menu-button", {
								"is-active": editor.isActive("bold"),
							})}
							onClick={toggleBold}
							type="button"
						>
							<Icons.Bold />
						</button>
						<button
							className={classNames("menu-button", {
								"is-active": editor.isActive("underline"),
							})}
							onClick={toggleUnderline}
							type="button"
						>
							<Icons.Underline />
						</button>
						<button
							className={classNames("menu-button", {
								"is-active": editor.isActive("intalic"),
							})}
							onClick={toggleItalic}
							type="button"
						>
							<Icons.Italic />
						</button>
						<button
							className={classNames("menu-button", {
								"is-active": editor.isActive("strike"),
							})}
							onClick={toggleStrike}
							type="button"
						>
							<Icons.Strikethrough />
						</button>
						<button
							className={classNames("menu-button", {
								"is-active": editor.isActive("code"),
							})}
							onClick={toggleCode}
							type="button"
						>
							<Icons.Code />
						</button>

						<button
							className={classNames("menu-button", {
								"is-active": editor.isActive("link"),
							})}
							onClick={setLink}
							type="button"
						>
							<Icons.Link />
						</button>
					</div>
					<div className="flex flex-row gap-3 px-2">
						<button
							onClick={toggleBulletList}
							className={classNames("", {
								"is-active": editor.isActive("bulletList"),
							})}
							type="button"
						>
							<Icons.Bullet />
						</button>
						<button
							onClick={toggleOrderedList}
							className={classNames("", {
								"is-active": editor.isActive("orderList"),
							})}
							type="button"
						>
							<Icons.Order />
						</button>
						<button
							onClick={toggleLeft}
							className={classNames("", {
								"is-active": editor.isActive("left"),
							})}
							type="button"
						>
							<Icons.Left />
						</button>
						<button
							onClick={toggleRight}
							className={classNames("", {
								"is-active": editor.isActive("right"),
							})}
							type="button"
						>
							<Icons.Right />
						</button>
						<button
							onClick={toggleCenter}
							className={classNames("", {
								"is-active": editor.isActive("center"),
							})}
							type="button"
						>
							<Icons.Center />
						</button>
						<button
							onClick={toggleJustify}
							className={classNames("", {
								"is-active": editor.isActive("justify"),
							})}
							type="button"
						>
							<Icons.Justify />
						</button>

						<button onClick={setHardBreak} className="menu-button" type="button">
							<IconCornerDownLeft className=" h-4 w-4" />
						</button>
					</div>
					<div className="flex flex-row gap-3 px-2">
						<button className="menu-button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} type="button">
							<Icons.RotateLeft />
						</button>
						<button className="menu-button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} type="button">
							<Icons.RotateRight />
						</button>

						<button
							onClick={insertTable}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconTablePlus className="h-4 w-4" />
						</button>

						<button
							onClick={deleteTable}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconTableMinus className="h-4 w-4" />
						</button>

						<button
							onClick={addColumnBefore}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconColumnInsertLeft className="h-4 w-4" />
						</button>

						<button
							onClick={addColumnAfter}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconColumnInsertRight className="h-4 w-4" />
						</button>

						<button
							onClick={deleteColumn}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconColumnRemove className="h-4 w-4" />
						</button>

						<button
							onClick={addRowBefore}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconRowInsertTop className="h-4 w-4" />
						</button>

						<button
							onClick={addRowAfter}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconRowInsertBottom className="h-4 w-4" />
						</button>

						<button
							onClick={deleteRow}
							className={classNames("", {
								"is-active": editor.isActive("table"),
							})}
							type="button"
						>
							<IconRowRemove className="h-4 w-4" />
						</button>

						<button
							onClick={deleteRow}
							className={classNames("", {
								"is-active": editor.isActive("youtube"),
							})}
							type="button"
						>
							<IconRowRemove className="h-4 w-4" />
						</button>
					</div>
				</div>
			</div>

			<div className="flex p-1 border-b border-border rounded-t-md bg-slate-50">
				<div className="flex overflow-y-scroll flex-row gap-2 items-center px-2 divide-x divide-neutral-900/20">
					<div className="flex flex-row gap-3 px-2">
						<input
							id="width"
							type="number"
							min="320"
							max="1024"
							placeholder="width"
							value={width}
							className="text-sm p-1"
							onChange={(event) => setWidth(event.target.value)}
						/>
						<input
							id="height"
							type="number"
							min="180"
							max="720"
							placeholder="height"
							value={height}
							className="text-sm p-1"
							onChange={(event) => setHeight(event.target.value)}
						/>

						<button
							onClick={addYoutubeVideo}
							className={classNames("", {
								"is-active": editor.isActive("youtube"),
							})}
							type="button"
						>
							<IconBrandYoutube className="h-4 w-4" />
						</button>
					</div>
				</div>
			</div>

			<BubbleMenu
				// className="bubble-menu-light"
				className="flex flex-row gap-2 py-1 px-2 border text-sm rounded-md bg-white"
				tippyOptions={{ duration: 150 }}
				editor={editor}
				shouldShow={({ editor, view, state, oldState, from, to }) => {
					// only show the bubble menu for links.
					return from === to && editor.isActive("link");
				}}
			>
				<button type="button" className="button py-1 px-3 rounded-md hover:bg-neutral-200" onClick={setLink}>
					Edit
				</button>
				<button type="button" className="py-1 px-3 rounded-md text-red-600 hover:bg-neutral-200" onClick={removeLink}>
					Remove
				</button>
			</BubbleMenu>

			{/* editor body */}
			<div className="rounded-b-md p-3 bg-white">
				<EditorContent editor={editor} contentEditable={!disabled} className="min-h-[400px] p-2 text-gray-800" />
			</div>
		</div>
	);
}
